import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import Revenue from './components/RevenueExpenses';
import Contracts from './components/Contracts';
import SendMoney from '../../components/SendMoney';
import FinanceBalance from '../../components/FinanceBalance';
import { FinanceChart } from '../../components/FinanceChart';
import Transactions from './components/Transactions';
import FallbackContentAnimation from '../../components/FallbackContentAnimation';
import {
  SendMoneyPreloader,
  TransactionsPreloader,
  CashChartPreloader,
  RevenuesPreloader,
} from '../../components/ContentPreloaders';

import { shouldAutoStartTour, tourStorageValue } from '../../helpers/tour/locationToStorage';

import {
  isTransactionDataLoaded,
  isRevenueAndExpansesDataLoaded,
  getChart,
  getTeamTotalSummary,
  getTransactionsData,
} from '../../redux/selector/Finance';
import { isLeaguesListLoaded } from '../../redux/selector/League';
import { getTourState } from '../../redux/selector/TourControl';

import {
  financeGetDataAboutRevenueAndExpenses,
  financeGetDataAboutTransaction,
  getFinanceChartData,
} from '../../redux/actions/Finance';
import { allowTour } from '../../redux/actions/TourControl';
import { getLeaguesList } from '../../redux/actions/League';
import { cashFormat } from '../../service/functions';
import {
  StyledLeftContainer,
  StyledRightContainer,
  StyledNewWrapper,
  StyledTopSection,
  StyledPreloaderContainer,
  StyledTransactionPreloaderContainer,
} from './styled';

function FinanceOverview({
  revenueLoading,
  chartLoading,
  transactionsLoading,
  sendMoneyLoading,
  current_cash,
  total_earnings,
  total_spendings,
  chartData,
  leagueList,
}) {
  return (
    <>
      <StyledLeftContainer className="first">
        <StyledTopSection>
          <FinanceBalance
            isLoading={revenueLoading}
            current_cash={cashFormat(current_cash?.toFixed())}
            total_earnings={cashFormat(total_earnings?.toFixed())}
            total_spendings={cashFormat(total_spendings?.toFixed())}
          />

          <FallbackContentAnimation
            loading={chartLoading}
            fallback={
              <StyledPreloaderContainer>
                <CashChartPreloader style={{ width: '100%' }} />
              </StyledPreloaderContainer>
            }
          >
            <FinanceChart chartData={chartData} />
          </FallbackContentAnimation>
        </StyledTopSection>

        <FallbackContentAnimation
          loading={revenueLoading}
          repeat={10}
          fallback={
            <StyledTransactionPreloaderContainer>
              <RevenuesPreloader style={{ width: '100%' }} />
            </StyledTransactionPreloaderContainer>
          }
        >
          <Revenue />
        </FallbackContentAnimation>
      </StyledLeftContainer>
      <StyledRightContainer>
        <FallbackContentAnimation
          loading={sendMoneyLoading}
          fallback={
            <StyledPreloaderContainer>
              <SendMoneyPreloader style={{ width: '100%' }} />
            </StyledPreloaderContainer>
          }
        >
          <SendMoney leagueList={leagueList} />
        </FallbackContentAnimation>

        <FallbackContentAnimation
          loading={transactionsLoading}
          repeat={10}
          fallback={
            <StyledTransactionPreloaderContainer>
              <TransactionsPreloader style={{ width: '100%' }} />
            </StyledTransactionPreloaderContainer>
          }
        >
          <Transactions />
        </FallbackContentAnimation>
      </StyledRightContainer>
    </>
  );
}

export default function Finance() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isLoadedTransaction = useSelector(isTransactionDataLoaded);
  const isLoadedRevenue = useSelector(isRevenueAndExpansesDataLoaded);
  const { current_cash, total_earnings, total_spendings } =
    useSelector(getTeamTotalSummary);
  const leagueList = useSelector(isLeaguesListLoaded);
  const chartData = useSelector(getChart);
  const transactions = useSelector(getTransactionsData);

  useEffect(() => {
    if (location.pathname === '/finance' || location.pathname === '/finance/') {
      history.replace('/finance/overview');
    }
  }, [history, location.pathname]);

  useEffect(() => {
    dispatch(financeGetDataAboutTransaction());
    dispatch(financeGetDataAboutRevenueAndExpenses());
    dispatch(getLeaguesList());
    dispatch(getFinanceChartData());
  }, [dispatch]);

  const hasBalance = current_cash != null;
  const hasChart = Boolean(chartData);
  const hasLeagues = Array.isArray(leagueList) && leagueList.length > 0;
  const hasTransactions =
    transactions &&
    typeof transactions === 'object' &&
    Object.keys(transactions).length > 0;
  const revenueLoading = isLoadedRevenue !== false && !hasBalance;
  const chartLoading = !hasChart;
  const transactionsLoading = isLoadedTransaction !== false && !hasTransactions;
  const sendMoneyLoading = !hasLeagues;
  const pageReady = hasBalance && hasChart && hasLeagues;
  const isTourStart = useSelector(getTourState);
  useEffect(() => {
    const activeLocalStorageTourState = localStorage.getItem(
      tourStorageValue[location.pathname]
    );
    if (pageReady && !isTourStart && !activeLocalStorageTourState && shouldAutoStartTour()) {
      dispatch(allowTour(true));
    }
  }, [pageReady, isTourStart, dispatch, location.pathname]);

  return (
    <StyledNewWrapper>
      <Switch>
        <Route
          path="/finance/contracts"
          render={() => <Contracts />}
        />
        <Route
          path="/finance/overview"
          render={() => (
            <FinanceOverview
              revenueLoading={revenueLoading}
              chartLoading={chartLoading}
              transactionsLoading={transactionsLoading}
              sendMoneyLoading={sendMoneyLoading}
              current_cash={current_cash}
              total_earnings={total_earnings}
              total_spendings={total_spendings}
              chartData={chartData}
              leagueList={leagueList}
            />
          )}
        />
      </Switch>
    </StyledNewWrapper>
  );
}
